import React, { useState } from 'react';
import { Wand2 } from 'lucide-react';

type TeamStyle = 'cosmic' | 'urban' | 'mythical' | 'tech';

const cosmicPrefixes = ['Stellar', 'Cosmic', 'Galactic', 'Nova', 'Astral', 'Solar', 'Quantum', 'Nebula'];
const urbanPrefixes = ['Metro', 'City', 'Street', 'Urban', 'Night', 'Shadow', 'Neon', 'Cyber'];
const mythicalPrefixes = ['Ancient', 'Mystic', 'Arcane', 'Divine', 'Eternal', 'Legend', 'Phoenix', 'Dragon'];
const techPrefixes = ['Cyber', 'Tech', 'Mech', 'Digital', 'Nano', 'Circuit', 'Power', 'Vector'];

const cosmicSuffixes = ['Guardians', 'Defenders', 'Protectors', 'Warriors', 'Knights', 'Sentinels', 'Legion', 'Force'];
const urbanSuffixes = ['Watchers', 'Shields', 'Patrol', 'Squad', 'Team', 'Corps', 'Unit', 'Alliance'];
const mythicalSuffixes = ['Order', 'Council', 'Circle', 'Masters', 'Sages', 'Chosen', 'Elite', 'Brotherhood'];
const techSuffixes = ['Division', 'Initiative', 'Network', 'Command', 'Prime', 'Core', 'Systems', 'Protocol'];

const generateNames = (style: TeamStyle, keywords: string): string[] => {
  let prefixes: string[];
  let suffixes: string[];

  switch (style) {
    case 'cosmic':
      prefixes = cosmicPrefixes;
      suffixes = cosmicSuffixes;
      break;
    case 'urban':
      prefixes = urbanPrefixes;
      suffixes = urbanSuffixes;
      break;
    case 'mythical':
      prefixes = mythicalPrefixes;
      suffixes = mythicalSuffixes;
      break;
    case 'tech':
      prefixes = techPrefixes;
      suffixes = techSuffixes;
      break;
  }

  const names: string[] = [];
  for (let i = 0; i < 5; i++) {
    const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
    const suffix = suffixes[Math.floor(Math.random() * suffixes.length)];
    const keyword = keywords ? ` ${keywords} ` : ' ';
    names.push(`${prefix}${keyword}${suffix}`);
  }

  return names;
};

const SuperheroNameGenerator: React.FC = () => {
  const [style, setStyle] = useState<TeamStyle>('cosmic');
  const [keywords, setKeywords] = useState('');
  const [generatedNames, setGeneratedNames] = useState<string[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]);

  const handleGenerate = () => {
    const names = generateNames(style, keywords);
    setGeneratedNames(names);
  };

  const toggleFavorite = (name: string) => {
    setFavorites(prev => 
      prev.includes(name) 
        ? prev.filter(n => n !== name)
        : [...prev, name]
    );
  };

  return (
    <section id="superhero-generator" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <span className="bg-violet-100 text-violet-800 px-4 py-1 rounded-full text-sm font-medium">
            Superhero Team Names
          </span>
        </div>
        
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
          Superhero Team Name Generator
        </h2>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Team Style
              </label>
              <select
                value={style}
                onChange={(e) => setStyle(e.target.value as TeamStyle)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 bg-white"
              >
                <option value="cosmic">Cosmic</option>
                <option value="urban">Urban</option>
                <option value="mythical">Mythical</option>
                <option value="tech">Tech-Based</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Keywords (Optional)
              </label>
              <input
                type="text"
                value={keywords}
                onChange={(e) => setKeywords(e.target.value)}
                placeholder="E.g., Justice, Power, Light"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 bg-white"
              />
            </div>
          </div>

          <div className="flex justify-center gap-4">
            <button
              onClick={handleGenerate}
              className="bg-violet-600 text-white py-4 px-8 rounded-lg hover:bg-violet-700 transition-colors flex items-center gap-2"
            >
              <Wand2 className="w-5 h-5" />
              Generate Names
            </button>
          </div>

          {generatedNames.length > 0 && (
            <div className="mt-12">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">
                Generated Names:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                {generatedNames.map((name, index) => (
                  <div
                    key={index}
                    className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between"
                  >
                    <span className="text-lg">{name}</span>
                    <button
                      onClick={() => toggleFavorite(name)}
                      className={`ml-4 p-2 rounded-full transition-colors ${
                        favorites.includes(name)
                          ? 'text-yellow-500 hover:text-yellow-600'
                          : 'text-gray-400 hover:text-gray-500'
                      }`}
                    >
                      ★
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 text-center text-sm text-gray-500">
            <p className="mb-2">Pro tip: Click the star icon to save your favorite names!</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SuperheroNameGenerator;